/**
 * Director SickClusteringPanel — same-team sick-leave pattern flags.
 *
 * Pure presentational: accepts `clusters[]` from the parent page (fetched
 * via the existing `/api/v1/insights/sick-clustering` endpoint which
 * Director can access as an HOD+ role). The Director sees the org-wide
 * picture since RLS grants visibility to all teams.
 *
 * Self-clears when clusters is empty. No × button.
 */
import type { CSSProperties } from 'react'
import { format } from 'date-fns'
import { Card } from '../primitives/Card'

export interface SickCluster {
  /** Unique key — `teamId-date`. */
  team: string
  employees: string[]
  /** ISO date (YYYY-MM-DD) of the cluster day. */
  window: string
  count: number
}

export interface SickClusteringPanelProps {
  clusters: SickCluster[]
  /** When true, renders a loading placeholder. */
  loading?: boolean
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso + 'T00:00:00')
    if (Number.isNaN(d.getTime())) return iso
    return format(d, 'EEE d MMM')
  } catch {
    return iso
  }
}

/**
 * Director-context sick-clustering signal panel.
 *
 * Lists team+day clusters where ≥N employees were on sick leave on the
 * same day. Provides the Director with an at-a-glance cross-team
 * illness signal without requiring a drill-down into individual teams.
 */
export function SickClusteringPanel({
  clusters,
  loading = false,
}: SickClusteringPanelProps): JSX.Element | null {
  if (!loading && clusters.length === 0) return null

  return (
    <Card padding="14px 18px" data-testid="director-sick-clustering-panel">
      <div style={headerStyle}>
        <h2 style={titleStyle}>Sick-leave clusters</h2>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
          {clusters.length} cluster{clusters.length === 1 ? '' : 's'} detected
        </span>
      </div>

      {loading ? (
        <div style={emptyStyle}>Loading…</div>
      ) : (
        <ul role="list" style={listStyle}>
          {clusters.map((c) => (
            <li
              key={`${c.team}-${c.window}`}
              data-testid="director-sick-cluster-row"
              style={rowStyle}
            >
              <div style={rowBodyStyle}>
                <div style={colMainStyle}>
                  <span style={teamNameStyle}>{c.team}</span>
                  <span style={countChipStyle} data-testid="director-sick-cluster-count">
                    {c.count} on sick leave
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                  {fmtDate(c.window)}
                  {c.employees.length > 0 ? (
                    <>
                      {' · '}
                      {c.employees.slice(0, 4).join(', ')}
                      {c.employees.length > 4
                        ? `, +${c.employees.length - 4} more`
                        : ''}
                    </>
                  ) : null}
                </div>
              </div>
              <span style={clusterBadgeStyle} aria-label="Cluster detected">
                Cluster
              </span>
            </li>
          ))}
        </ul>
      )}

      {!loading && clusters.length > 0 ? (
        <div style={captionStyle}>
          Cross-team illness spread or workload pressure — worth a quiet check-in.
        </div>
      ) : null}
    </Card>
  )
}

// ── styles ────────────────────────────────────────────────────────────────────

const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: 10,
}

const titleStyle: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 15,
  fontWeight: 600,
  letterSpacing: '-0.01em',
  margin: 0,
}

const listStyle: CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: 'none',
}

const rowStyle: CSSProperties = {
  padding: '10px 0',
  borderBottom: '1px solid var(--line-subtle)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 12,
}

const rowBodyStyle: CSSProperties = {
  minWidth: 0,
  flex: 1,
}

const colMainStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
}

const teamNameStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
}

const countChipStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--text-2)',
}

const clusterBadgeStyle: CSSProperties = {
  flexShrink: 0,
  fontSize: 11,
  fontFamily: 'var(--font-mono)',
  color: 'var(--warning-fg)',
  background: 'var(--warning-bg)',
  border: '1px solid var(--warning-line)',
  borderRadius: 6,
  padding: '2px 8px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
}

const captionStyle: CSSProperties = {
  marginTop: 10,
  fontSize: 12,
  color: 'var(--text-3)',
  lineHeight: 1.5,
}

const emptyStyle: CSSProperties = {
  padding: '20px 0',
  textAlign: 'center',
  color: 'var(--text-3)',
  fontSize: 13,
}
